import React, { ReactNode, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "@/assets/dinamo-logo.svg";
import { ReactComponent as CatalogueIcon } from "@/assets/icons/catalogue.svg";
import { ReactComponent as PublicationIcon } from "@/assets/icons/publication.svg";
import { ReactComponent as CartIcon } from "@/assets/icons/cart.svg";
import { ReactComponent as ProfileIcon } from "@/assets/icons/profile.svg";
import { Tabs, Indicator, Button } from "@mantine/core";
import { ReactComponent as LogoutIcon } from "@/assets/icons/logout.svg";
import GlobalSVGDefs from "../Shared/GlobalSVGDefs";
import LinksFooter from "../Shared/LinksFooter";
import { useCart } from "@/context/CartContext";
import { useMe } from "@/context/MeContext";
import "./Layout.css";
import clsx from "clsx";

interface UserLayoutProps {
  children: ReactNode;
}

const UserLayout: React.FC<UserLayoutProps> = ({ children }) => {
  const { me, logout } = useMe();
  const navigate = useNavigate();

  const { cart } = useCart();
  const pages = ["publications", "catalogue", "cart", "profile"];
  const location = useLocation();
  const activeTab =
    pages.find((page) => location.pathname.includes(page)) || "";

  const goToPage = (value: string | null) => {
    if (value) {
      navigate(`/${value}`);
    }
  };

  const goHome = () => {
    navigate("/catalogue");
  };

  const logoutHandler = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!me || me?.type === "ADMIN") {
      navigate("/");
    }
  });

  return (
    <>
      <GlobalSVGDefs />
      <nav className="header">
        <div
          className="flex gap-3 items-center cursor-pointer"
          onClick={goHome}
        >
          <Logo />
          <div className="text-[#004a99] text-base font-bold">
            Digitalne revije
          </div>
        </div>
        <div className="flex items-center">
          <Tabs
            value={activeTab}
            onChange={goToPage}
            variant="pills"
            className="items-center"
            color="#004a99"
          >
            <Tabs.List justify="end" className="!gap-[1rem]">
              <Tabs.Tab
                value="catalogue"
                className={clsx(
                  "!py-[1.25rem] !px-0 mx-4 !bg-[transparent] font-medium",
                  activeTab !== "catalogue" && "text-button"
                )}
                leftSection={
                  <CatalogueIcon
                    className={
                      activeTab === "catalogue"
                        ? "text-[#004a99]"
                        : "text-[#525252]"
                    }
                  />
                }
              >
                <span
                  className={
                    activeTab === "catalogue"
                      ? "tab-name text-[#004a99] font-bold"
                      : "tab-name"
                  }
                >
                  Revije
                </span>
              </Tabs.Tab>

              <Tabs.Tab
                value="publications"
                className={clsx(
                  "!py-[1.25rem] !px-0 mx-4 !bg-[transparent] font-medium",
                  activeTab !== "publications" && "text-button"
                )}
                leftSection={
                  <PublicationIcon
                    className={
                      activeTab === "publications"
                        ? "text-[#004a99]"
                        : "text-[#525252]"
                    }
                  />
                }
              >
                <span
                  className={
                    activeTab === "publications"
                      ? "tab-name text-[#004a99] font-bold"
                      : "tab-name"
                  }
                >
                  Publikacije
                </span>
              </Tabs.Tab>

              <Tabs.Tab
                value="cart"
                className={clsx(
                  "!py-[1.25rem] !px-0 mx-4 !bg-[transparent] font-medium",
                  activeTab !== "cart" && "text-button"
                )}
                leftSection={
                  <Indicator
                    label={cart.length > 0 ? cart.length : null}
                    color={cart.length > 0 ? "#004a99" : "transparent"}
                    size={15}
                  >
                    <CartIcon
                      className={
                        activeTab === "cart"
                          ? "text-[#004a99] mr-1"
                          : "text-[#525252] mr-1"
                      }
                    />
                  </Indicator>
                }
              >
                <span
                  className={
                    activeTab === "cart"
                      ? "text-[#004a99] font-bold tab-name"
                      : "tab-name"
                  }
                >
                  Košarica
                </span>
              </Tabs.Tab>
              <Tabs.Tab
                value="profile"
                className={clsx(
                  "!py-[1.25rem] !px-0 mx-4 !bg-[transparent] font-medium",
                  activeTab !== "profile" && "text-button"
                )}
                leftSection={
                  <ProfileIcon
                    className={
                      activeTab === "profile"
                        ? "text-[#004a99]"
                        : "text-[#525252]"
                    }
                  />
                }
              >
                <span
                  className={
                    activeTab === "profile"
                      ? "text-[#004a99] font-bold tab-name"
                      : "tab-name"
                  }
                >
                  Profil
                </span>
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
          <div className="flex items-center ml-3">
            <div className="w-0.5 h-3 bg-[#D9D9D9] rounded-sm" />
          </div>
          <div className="flex items-center ml-3">
            <Button
              leftSection={<LogoutIcon />}
              variant="transparent"
              className="text-button"
              onClick={logoutHandler}
            >
              Odjava
            </Button>
          </div>
        </div>
      </nav>
      <div className="user-content scroll-container">
        {children}
        <LinksFooter activeTab={activeTab} />
      </div>

      <footer className="user-footer">
        <Tabs
          value={activeTab}
          onChange={goToPage}
          variant="pills"
          className="items-center !justify-between w-full"
          color="#004a99"
        >
          <Tabs.List justify="space-between">
            <Tabs.Tab
              value="catalogue"
              className="!py-[0.75rem] !px-0  !bg-[transparent] !flex-1"
            >
              <div className="!flex flex-col !gap-1 !items-center !justify-center">
                <CatalogueIcon
                  className={
                    activeTab === "catalogue"
                      ? "text-[#004a99]"
                      : "text-[#525252]"
                  }
                />
                <span
                  className={
                    activeTab === "catalogue" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Revije
                </span>
              </div>
            </Tabs.Tab>
            <Tabs.Tab
              value="publications"
              className="!py-[0.75rem] !px-0  !bg-[transparent] !flex-1"
            >
              <div className="!flex flex-col !gap-1 !items-center !justify-center">
                <PublicationIcon
                  className={
                    activeTab === "publications"
                      ? "text-[#004a99]"
                      : "text-[#525252]"
                  }
                />
                <span
                  className={
                    activeTab === "publications"
                      ? "text-[#004a99] font-bold"
                      : ""
                  }
                >
                  Publikacije
                </span>
              </div>
            </Tabs.Tab>
            <Tabs.Tab
              value="cart"
              className="!py-[0.75rem] !px-0 !bg-[transparent] !flex-1"
            >
              <div className="!flex flex-col !gap-1 !items-center !justify-center">
                <Indicator
                  label={cart.length > 0 ? cart.length : null}
                  color={cart.length > 0 ? "#004a99" : "transparent"}
                  size={15}
                >
                  <CartIcon
                    className={
                      activeTab === "cart" ? "text-[#004a99]" : "text-[#525252]"
                    }
                  />
                </Indicator>
                <span
                  className={
                    activeTab === "cart" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Košarica
                </span>
              </div>
            </Tabs.Tab>
            <Tabs.Tab
              value="profile"
              className="!py-[0.75rem] !px-0  !bg-[transparent] !flex-1"
            >
              <div className="!flex flex-col !gap-1 !items-center !justify-center">
                <ProfileIcon
                  className={
                    activeTab === "profile"
                      ? "text-[#004a99]"
                      : "text-[#525252]"
                  }
                />
                <span
                  className={
                    activeTab === "profile" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Profil
                </span>
              </div>
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </footer>
    </>
  );
};

export default UserLayout;
